/**
 * Futures-Spot Data Context
 *
 * Provides real-time price data for futures-spot arbitrage opportunities.
 * Integrates Epic 8's Binance Futures data with new spot exchange subscriptions.
 *
 * Architecture:
 * - REUSES Epic 8's FuturesDataContext for futures mark prices (no new client)
 * - ADDS CCXT clients for spot exchanges (Binance, Upbit, Bithumb)
 * - COMBINES both data sources with price differential calculations
 * - OPTIMIZES with snapshot pattern for high-frequency updates (100+ msg/sec)
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  useReducer,
  ReactNode,
} from 'react';
import { useFuturesData } from './FuturesDataContext';
import { useFuturesSpotMarkets } from '../hooks/useFuturesSpotMarkets';
import { useSpotPriceSubscriptions } from '../hooks/useSpotPriceSubscriptions';
import { Exchange } from '../types/futuresSpot';

/**
 * Futures-Spot Price Data Interface
 */
export interface FuturesSpotPriceData {
  /** Normalized symbol (e.g., "BTC/USDT") */
  symbol: string;

  /** Base currency (e.g., "BTC") */
  baseAsset: string;

  /** Quote currency (e.g., "USDT", "KRW") */
  quoteAsset: string;

  /** Spot exchange providing the price */
  spotExchange: Exchange;

  /** Last traded price on spot market */
  spotPrice: number | null;

  /** Mark price on Binance Futures */
  futuresPrice: number | null;

  /** Absolute price differential (futures - spot) */
  absoluteDifferential: number | null;

  /** Percentage price differential ((futures - spot) / spot * 100) */
  percentageDifferential: number | null;

  /** Current funding rate (e.g., 0.0001 = 0.01%) */
  fundingRate: number | null;

  /** UTC timestamp of next funding event (milliseconds) */
  nextFundingTime: number | null;

  /** Last update timestamp (milliseconds since epoch) */
  timestamp: number;
}

/**
 * Connection Status per Exchange
 */
type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

/**
 * Context State Interface
 */
interface FuturesSpotState {
  /** Map of symbol → price data (snapshot pattern cache) */
  prices: Map<string, FuturesSpotPriceData>;

  /** Connection status per exchange */
  connectionStatus: {
    binanceFutures: ConnectionStatus;
    binanceSpot: ConnectionStatus;
    upbit: ConnectionStatus;
    bithumb: ConnectionStatus;
  };

  /** Error messages per exchange */
  errors: Map<Exchange, string>;

  /** Loading state */
  loading: boolean;

  /** Last update timestamp */
  lastUpdate: number;
}

/**
 * Context Actions
 */
type FuturesSpotAction =
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_CONNECTION_STATUS'; exchange: Exchange; status: ConnectionStatus }
  | { type: 'SET_ERROR'; exchange: Exchange; error: string }
  | { type: 'CLEAR_ERROR'; exchange: Exchange }
  | { type: 'UPDATE_PRICES'; prices: Map<string, FuturesSpotPriceData> }
  | { type: 'UPDATE_SPOT_PRICE'; symbol: string; exchange: Exchange; price: number }
  | { type: 'UPDATE_FUTURES_PRICE'; symbol: string; price: number }
  | { type: 'UPDATE_FUNDING_DATA'; symbol: string; fundingRate: number; nextFundingTime: number };

/**
 * Reducer for state management
 */
function futuresSpotReducer(state: FuturesSpotState, action: FuturesSpotAction): FuturesSpotState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.loading };

    case 'SET_CONNECTION_STATUS': {
      // Map Exchange enum to connection status keys
      let statusKey: keyof typeof state.connectionStatus;
      switch (action.exchange) {
        case Exchange.Binance:
          statusKey = 'binanceSpot';
          break;
        case Exchange.BinanceFutures:
          statusKey = 'binanceFutures';
          break;
        case Exchange.Upbit:
          statusKey = 'upbit';
          break;
        case Exchange.Bithumb:
          statusKey = 'bithumb';
          break;
        default:
          console.warn('[FuturesSpotData] Unknown exchange:', action.exchange);
          return state;
      }

      return {
        ...state,
        connectionStatus: {
          ...state.connectionStatus,
          [statusKey]: action.status,
        },
      };
    }

    case 'SET_ERROR': {
      const newErrors = new Map(state.errors);
      newErrors.set(action.exchange, action.error);
      return { ...state, errors: newErrors };
    }

    case 'CLEAR_ERROR': {
      const newErrors = new Map(state.errors);
      newErrors.delete(action.exchange);
      return { ...state, errors: newErrors };
    }

    case 'UPDATE_PRICES':
      return {
        ...state,
        prices: action.prices,
        lastUpdate: Date.now(),
      };

    case 'UPDATE_FUNDING_DATA': {
      // This case is not used in the reducer pattern - funding data is merged in the sync effect
      // Keeping for completeness and future use
      return state;
    }

    default:
      return state;
  }
}

/**
 * Initial state
 */
const initialState: FuturesSpotState = {
  prices: new Map(),
  connectionStatus: {
    binanceFutures: 'disconnected',
    binanceSpot: 'disconnected',
    upbit: 'disconnected',
    bithumb: 'disconnected',
  },
  errors: new Map(),
  loading: true,
  lastUpdate: 0,
};

/**
 * Context Type
 */
interface FuturesSpotContextType {
  state: FuturesSpotState;
  getPriceData: (symbol: string) => FuturesSpotPriceData | undefined;
  getPriceList: () => FuturesSpotPriceData[];
}

const FuturesSpotContext = createContext<FuturesSpotContextType | undefined>(undefined);

/**
 * Snapshot update interval (33 fps = 30ms)
 * Prevents event loop blocking from high-frequency updates
 */
const SNAPSHOT_INTERVAL_MS = 30;

/**
 * Stale data threshold (60 seconds)
 * Data older than this will be evicted from cache
 */
const STALE_DATA_THRESHOLD_MS = 60000;

/**
 * Exponential backoff configuration for reconnection
 */
const INITIAL_RETRY_DELAY_MS = 1000; // 1 second
const MAX_RETRY_DELAY_MS = 60000; // 60 seconds
const BACKOFF_MULTIPLIER = 2;

/**
 * Futures-Spot Data Provider Component
 *
 * Coordinates all data sources:
 * 1. Epic 8's FuturesDataContext for Binance Futures mark prices
 * 2. CCXT spot exchange clients for Binance Spot, Upbit, Bithumb
 * 3. Price differential calculations
 * 4. High-frequency snapshot pattern for UI updates
 */
export const FuturesSpotDataProvider: React.FC<{ children: ReactNode; enabled?: boolean }> = ({
  children,
  enabled = true,
}) => {
  // State management with reducer
  const [state, dispatch] = useReducer(futuresSpotReducer, initialState);

  // REUSE Epic 8's futures data (CRITICAL: Don't create new client!)
  const { markPrices, connected: futuresConnected } = useFuturesData();

  // Get market discovery from Story 10.1
  const { markets, loading: marketsLoading } = useFuturesSpotMarkets({ enabled });

  // Cache for high-frequency updates (snapshot pattern)
  const priceCache = useRef<Map<string, FuturesSpotPriceData>>(new Map());

  /**
   * Calculate price differential with edge case handling
   */
  const calculateDifferential = useMemo(
    () =>
      (
        spotPrice: number | null,
        futuresPrice: number | null
      ): { absolute: number | null; percentage: number | null } => {
        // Handle null prices
        if (spotPrice === null || futuresPrice === null) {
          return { absolute: null, percentage: null };
        }

        // Handle zero spot price (avoid division by zero)
        if (spotPrice === 0) {
          return { absolute: futuresPrice, percentage: null };
        }

        const absolute = futuresPrice - spotPrice;
        const percentage = (absolute / spotPrice) * 100;

        // Handle NaN/Infinity
        if (!Number.isFinite(percentage)) {
          return { absolute, percentage: null };
        }

        return { absolute, percentage };
      },
    []
  );

  /**
   * Callback: Handle spot price updates from subscriptions
   */
  const handleSpotPriceUpdate = useCallback(
    (symbol: string, exchange: Exchange, price: number) => {
      // Use exchange-symbol key to allow multiple exchanges per symbol
      const cacheKey = `${exchange}-${symbol}`;
      // Get existing cached data or create new
      const existing = priceCache.current.get(cacheKey);

      if (existing) {
        // Update spot price and recalculate differential
        const diff = calculateDifferential(price, existing.futuresPrice);

        priceCache.current.set(cacheKey, {
          ...existing,
          spotExchange: exchange,
          spotPrice: price,
          absoluteDifferential: diff.absolute,
          percentageDifferential: diff.percentage,
          timestamp: Date.now(),
        });
      } else {
        // Create new entry (futures price and funding data will be filled by Epic 8)
        const [base, quote] = symbol.split('/');
        priceCache.current.set(cacheKey, {
          symbol,
          baseAsset: base,
          quoteAsset: quote,
          spotExchange: exchange,
          spotPrice: price,
          futuresPrice: null,
          absoluteDifferential: null,
          percentageDifferential: null,
          fundingRate: null,
          nextFundingTime: null,
          timestamp: Date.now(),
        });
      }
    },
    [calculateDifferential]
  );

  /**
   * Callback: Handle connection status changes
   */
  const handleConnectionStatus = useCallback(
    (exchange: Exchange, status: ConnectionStatus) => {
      dispatch({ type: 'SET_CONNECTION_STATUS', exchange, status });
    },
    []
  );

  /**
   * Callback: Handle errors
   */
  const handleError = useCallback((exchange: Exchange, error: string) => {
    dispatch({ type: 'SET_ERROR', exchange, error });

    console.error(`[FuturesSpotData] ${exchange} error:`, error);
  }, []);

  /**
   * Get exchange-specific symbols for subscriptions
   * CRITICAL: Use actual spot exchange symbols, not normalized format!
   * Example: Binance Spot has "BTC/USDT" but not "1000SATS/USDT" (futures-only)
   */
  const spotSymbolsByExchange = useMemo(() => {
    const binanceSymbols = new Set<string>();
    const upbitSymbols = new Set<string>();
    const bithumbSymbols = new Set<string>();

    markets.forEach((market) => {
      // Extract exchange-specific symbols from the spotSymbols map
      if (market.spotExchanges.includes(Exchange.Binance) && market.spotSymbols[Exchange.Binance]) {
        binanceSymbols.add(market.spotSymbols[Exchange.Binance]);
      }
      if (market.spotExchanges.includes(Exchange.Upbit) && market.spotSymbols[Exchange.Upbit]) {
        upbitSymbols.add(market.spotSymbols[Exchange.Upbit]);
      }
      if (market.spotExchanges.includes(Exchange.Bithumb) && market.spotSymbols[Exchange.Bithumb]) {
        bithumbSymbols.add(market.spotSymbols[Exchange.Bithumb]);
      }
    });

    const symbolsByExchange = {
      binance: Array.from(binanceSymbols),
      upbit: Array.from(upbitSymbols),
      bithumb: Array.from(bithumbSymbols),
    };

    console.log('[FuturesSpotDataContext] Spot symbols per exchange:', {
      binance: symbolsByExchange.binance.length,
      upbit: symbolsByExchange.upbit.length,
      bithumb: symbolsByExchange.bithumb.length,
    });

    return symbolsByExchange;
  }, [markets]);

  /**
   * Subscribe to spot exchange price updates
   */
  const totalSymbolCount = spotSymbolsByExchange.binance.length +
                           spotSymbolsByExchange.upbit.length +
                           spotSymbolsByExchange.bithumb.length;
  const subscriptionEnabled = enabled && !marketsLoading && totalSymbolCount > 0;

  // DEBUG: Store in window for inspection
  if (typeof window !== 'undefined') {
    (window as any).futuresSpotDebug = {
      enabled,
      marketsLoading,
      symbolsLength: totalSymbolCount,
      subscriptionEnabled,
      marketCount: markets.length,
      symbolsByExchange: {
        binance: spotSymbolsByExchange.binance.length,
        upbit: spotSymbolsByExchange.upbit.length,
        bithumb: spotSymbolsByExchange.bithumb.length,
      }
    };
  }

  useSpotPriceSubscriptions({
    symbolsByExchange: spotSymbolsByExchange,
    enabled: subscriptionEnabled,
    onPriceUpdate: handleSpotPriceUpdate,
    onConnectionStatus: handleConnectionStatus,
    onError: handleError,
    enableLogging: true, // Set to true for debugging
  });

  /**
   * Update futures connection status from Epic 8
   */
  useEffect(() => {
    const status: ConnectionStatus = futuresConnected ? 'connected' : 'disconnected';
    dispatch({
      type: 'SET_CONNECTION_STATUS',
      exchange: Exchange.BinanceFutures,
      status,
    });
  }, [futuresConnected]);

  /**
   * Sync futures prices and funding data from Epic 8 to cache
   * This processes Epic 8's mark prices and updates our cache with both price and funding info
   */
  useEffect(() => {
    if (!futuresConnected || markPrices.size === 0) return;

    // Process mark prices into cache
    markPrices.forEach((markPriceData, symbol) => {
      // Extract base asset from futures symbol (e.g., "BTC/USDT" -> "BTC")
      const [futuresBase] = symbol.split('/');
      let foundAny = false;

      // Iterate through all cache entries to find ones matching this base asset
      priceCache.current.forEach((value, key) => {
        // Match by base asset only (not full symbol, since quote currencies differ)
        // e.g., futures "BTC/USDT" matches spot "BTC/KRW" via base asset "BTC"
        if (value.baseAsset === futuresBase) {
          foundAny = true;

          // Update futures price, funding data, and recalculate differential
          // Use bestBid (best bid price from book ticker) for futures price
          // This is the price we'd get when opening a short position
          const futuresPrice = markPriceData.bestBid > 0 ? markPriceData.bestBid : markPriceData.lastPrice;
          const diff = calculateDifferential(value.spotPrice, futuresPrice);

          priceCache.current.set(key, {
            ...value,
            futuresPrice,
            absoluteDifferential: diff.absolute,
            percentageDifferential: diff.percentage,
            fundingRate: markPriceData.fundingRate,
            nextFundingTime: markPriceData.nextFundingTime,
            timestamp: Date.now(),
          });
        }
      });

      // If no entries exist for this symbol yet, create a default entry
      if (!foundAny) {
        const [base, quote] = symbol.split('/');
        const cacheKey = `${Exchange.Binance}-${symbol}`; // Default to Binance
        const futuresPrice = markPriceData.bestBid > 0 ? markPriceData.bestBid : markPriceData.lastPrice;

        priceCache.current.set(cacheKey, {
          symbol,
          baseAsset: base,
          quoteAsset: quote,
          spotExchange: Exchange.Binance,
          spotPrice: null,
          futuresPrice,
          absoluteDifferential: null,
          percentageDifferential: null,
          fundingRate: markPriceData.fundingRate,
          nextFundingTime: markPriceData.nextFundingTime,
          timestamp: Date.now(),
        });
      }
    });
  }, [markPrices, futuresConnected, calculateDifferential]);

  /**
   * Snapshot Pattern: Fixed-rate UI updates (33 fps)
   * Copies cache to React state every 30ms to prevent event loop blocking
   */
  useEffect(() => {
    if (!enabled) return;

    console.info('[FuturesSpotData] Starting snapshot updates (30ms interval)');

    const snapshotInterval = setInterval(() => {
      // Performance monitoring: Start timing
      const start = performance.now();

      // Evict stale data from cache
      const now = Date.now();
      const keysToDelete: string[] = [];
      priceCache.current.forEach((value, key) => {
        if (now - value.timestamp > STALE_DATA_THRESHOLD_MS) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => priceCache.current.delete(key));

      if (keysToDelete.length > 0) {
        console.info(`[FuturesSpotData] Evicted ${keysToDelete.length} stale entries from cache`);
      }

      // Create snapshot from cache
      const snapshot = new Map(priceCache.current);

      // Update React state with snapshot
      dispatch({
        type: 'UPDATE_PRICES',
        prices: snapshot,
      });

      // Performance monitoring: Log slow operations
      const elapsed = performance.now() - start;
      if (elapsed > 10) {
        console.warn(`[FuturesSpotData] Slow snapshot generation: ${elapsed.toFixed(2)}ms`);
      }
    }, SNAPSHOT_INTERVAL_MS);

    return () => {
      console.info('[FuturesSpotData] Stopping snapshot updates');
      clearInterval(snapshotInterval);
    };
  }, [enabled]);

  /**
   * Set loading to false once markets are loaded
   */
  useEffect(() => {
    if (!marketsLoading && markets.length > 0) {
      dispatch({ type: 'SET_LOADING', loading: false });
      console.info(`[FuturesSpotData] Markets loaded: ${markets.length} symbols`);
    }
  }, [marketsLoading, markets.length]);

  /**
   * Context value with memoized helpers
   */
  const value = useMemo(
    () => ({
      state,
      getPriceData: (symbol: string) => state.prices.get(symbol),
      getPriceList: () => Array.from(state.prices.values()),
    }),
    [state]
  );

  return <FuturesSpotContext.Provider value={value}>{children}</FuturesSpotContext.Provider>;
};

/**
 * Custom Hook: useFuturesSpotData
 *
 * Returns futures-spot price data context.
 *
 * @throws Error if used outside FuturesSpotDataProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useFuturesSpotData = () => {
  const context = useContext(FuturesSpotContext);
  if (!context) {
    throw new Error('useFuturesSpotData must be used within FuturesSpotDataProvider');
  }
  return context;
};
